import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Observable, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, last, map, switchMap, tap } from 'rxjs/operators';
import { ConfirmModalComponent } from '../../../common/components/confirm-modal/confirm-modal.component';
import { DateTimeHelper } from '../../../common/helpers/datetime/datetime.helper';
import { CoverLetter, CurrencyCollection, Document, UnitsCollection } from '../../../common/models';
import { TenderType } from '../../../common/models/tender-type';
import { CoverLetterService, TenderDocumentsService } from '../../../common/services';
import { Timezone, TimezonesCollection } from '../../../geodata/models';
import { ToastService } from '../../../main/services';
import { createTenderForm, getValidationMessage, getValidationStringMessage } from '../../forms/tender.form';
import { Tender } from '../../models';
import { TenderService } from '../../services';

@Component({
    selector: 'jft-tender-details',
    templateUrl: './create-tender-details.component.html',
    styleUrls: ['./create-tender-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateTenderDetailsComponent implements OnInit {

    public tender: Tender;
    public coverLetter: CoverLetter;
    public form: UntypedFormGroup;
    public formErrors: string;

    public timezonesCollection: TimezonesCollection;
    public unitsCollection: UnitsCollection;
    public tenderTypes: TenderType[];
    public currencyCollection: CurrencyCollection;

    // Document handling
    public documents: Document[] = [];
    public uploadInProgress = false;

    // Cover Letter handling
    public coverLetterText: string = '';
    public coverLetterFile: Document;
    public coverLetterType: 'text' | 'file' = 'text';
    public coverLetterUploadInProgress = false;

    public getValidationMessage = getValidationMessage;

    constructor(
        private tenderService: TenderService,
        private route: ActivatedRoute,
        private router: Router,
        private toast: ToastService,
        private coverLetterService: CoverLetterService,
        private tenderDocumentsService: TenderDocumentsService,
        private ngbModal: NgbModal,
    ) {
    }

    public ngOnInit() {
        let data = this.route.snapshot.data,
            parentData = this.route.parent.snapshot.data;

        this.tender = parentData['tender'];
        this.timezonesCollection = data['timezonesCollection'];
        this.unitsCollection = data['unitsCollection'];
        this.currencyCollection = data['currencyCollection'];
        // Load documents from resolver
        this.documents = data['documents'] || [];

        this.tenderTypes = data['tenderTypes'];

        this.coverLetter = data['coverLetter'];

        // Initialize cover letter state
        if (this.coverLetter) {
            this.coverLetterText = this.coverLetter.text || '';
            this.coverLetterFile = this.coverLetter.file;
            if (this.coverLetterFile) {
                this.coverLetterType = 'file';
            } else {
                this.coverLetterType = 'text';
            }
        }

        this.form = createTenderForm(this.tender, this.coverLetter);

        this.formOnChanges();
        this.form.updateValueAndValidity();
    }

    public formOnChanges(): void {
        this.formErrors = getValidationStringMessage(this.form);
        this.form.valueChanges.subscribe(val => {
            this.formErrors = getValidationStringMessage(this.form);
        });
    }

    public searchTimezone = (text$: Observable<string>) => {
        return text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map(term => term.length < 2 ? [] : this.timezonesCollection.search(term))
        );
    };

    public formatter(timezone: Timezone) {
        return timezone.name;
    }

    public onSubmit() {
        this.form.disable();

        // Update cover letter model text from UI bound variable
        if (this.coverLetter && this.coverLetterType === 'text') {
            this.coverLetter.text = this.coverLetterText;
        }

        // Built now, not in switchMap: while the letter saves, the datepickers' validator
        // change rebuilds form.value from the controls (raw NgbDateStructs → 400).
        const details = this.convertDataBeforeSubmit();

        // The letter first, then submit-invitation: the server refuses a step without a
        // letter, and sent in parallel the submit could land before the letter (Plan 8 I5).
        const letterSaved: Observable<unknown> = this.coverLetter && this.coverLetterType === 'text'
            ? this.coverLetterService.update(this.tender, this.coverLetter)
            : of(null);

        letterSaved.pipe(
            last(),
            switchMap(() => this.tenderService.update(this.tender, details))
        ).subscribe({
            next: () => {
                this.form.enable();
                this.toast.success(`Tender updated`);
                this.router.navigate(['/tenders/new/', this.tender.id, 'locations']);
            },
            error: (e) => {
                this.form.enable();
                this.toast.error(e);
            }
        });
    }

    private convertDataBeforeSubmit() {
        const value = this.form.value;
        return {
            ...value,
            date_contract_start: DateTimeHelper.convertToDateFormat(value.date_contract_start),
            date_contract_end: DateTimeHelper.convertToDateFormat(value.date_contract_end),
            date_round_end: DateTimeHelper.convertToDateFormat(value.date_round_end)
        };
    }

    get minDateRoundEnd(): any {
        return this.getMinDatepickerDate(moment());
    }

    get minStartDate(): any {
        return this.getMinDatepickerDate(this.form.value.date_round_end || moment());
    }

    get minEndDate(): any {
        return this.getMinDatepickerDate(this.form.value.date_contract_start || moment());
    }

    getMinDatepickerDate = (date: any): any => {
        return DateTimeHelper.convertToDatepickerDate(date, 'add');
    }

    public getFormGroupErrorClasses(control: AbstractControl): {} {
        return { 'is-invalid': control.invalid && control.touched };
    }

    public showErrorBlock(control: AbstractControl): boolean {
        return control.errors && (control.dirty || control.touched);
    }

    // Cover Letter Methods
    public setCoverLetterType(type: 'text' | 'file'): void {
        this.coverLetterType = type;
        this.updateCoverLetterFormValue();
    }

    public updateCoverLetterFormValue(): void {
        const value = {
            text: this.coverLetterType === 'text' ? this.coverLetterText : '',
            file: this.coverLetterType === 'file' ? this.coverLetterFile : null
        };
        this.form.patchValue({
            cover_letter: value
        });
        this.form.markAsDirty();
    }

    public uploadCoverLetter(files: File[]): void {
        if (!files || !files.length) {
            return;
        }

        const file = files[0];
        // 10MB limit check
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            this.toast.error(`File is too large. Maximum size allowed is 10MB.`);
            return;
        }

        this.coverLetterUploadInProgress = true;

        this.coverLetterService.uploadDocument(this.tender, this.coverLetter, file)
            .pipe(tap(() => this.coverLetterUploadInProgress = false))
            .subscribe({
                next: (res) => {
                    // Result might be different depending on service, adjust if needed
                    // CoverLetterUploaderComponent logic suggests it updates the coverLetter.file
                    this.coverLetterFile = this.coverLetter.file;
                    this.toast.success(`Cover letter uploaded successfully`);

                    this.updateCoverLetterFormValue();
                },
                error: (e) => {
                    this.coverLetterUploadInProgress = false;
                    this.toast.error(`Failed to upload cover letter: ${e}`);
                }
            });
    }

    public removeCoverLetter(): void {
        if (!this.coverLetterFile) return;

        this.coverLetterService.removeDocument(this.tender, this.coverLetter, this.coverLetterFile)
            .subscribe({
                next: () => {
                    this.toast.success(`Cover letter removed successfully`);
                    this.coverLetterFile = null;
                    if (this.coverLetter) {
                        this.coverLetter.file = null;
                    }
                    this.updateCoverLetterFormValue();
                },
                error: (e) => {
                    this.toast.error(e);
                }
            });
    }


    // Documents Methods
    public uploadDocuments(files: File[] | FileList): void {
        const fileList: File[] = Array.isArray(files) ? files : Array.from(files || []);
        if (!fileList || !fileList.length) {
            return;
        }

        this.uploadInProgress = true;

        // 10MB limit check
        const maxSize = 10 * 1024 * 1024;
        const validFiles = fileList.filter(f => {
            if (f.size > maxSize) {
                this.toast.error(`File "${f.name}" is too large. Maximum size allowed is 10MB.`);
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) {
            this.uploadInProgress = false;
            return;
        }

        let observables = [];
        for (let file of validFiles) {
            observables.push(this.tenderDocumentsService.create(this.tender, file));
        }

        merge(...observables).subscribe({
            next: (document: Document) => {
                this.documents.push(document);
                this.toast.success(`Document: ${document.name} has been uploaded`);
            },
            error: (e) => {
                this.toast.error(e);
                this.uploadInProgress = false; // Note: this might turn off early if one fails
            },
            complete: () => {
                this.uploadInProgress = false;
            }
        });
    }

    public removeDocument(document: Document): void {
        this.tenderDocumentsService.remove(this.tender, document).subscribe({
            next: () => {
                const index = this.documents.indexOf(document);
                this.documents.splice(index, 1);
                this.toast.success(`Document removed successfully`);
            },
            error: (e) => {
                this.toast.error(e);
            }
        });
    }

    public deleteTender(): void {
        const modalRef = this.ngbModal.open(ConfirmModalComponent);
        modalRef.componentInstance.title = 'Delete Tender';
        modalRef.componentInstance.text = 'Are you sure you want to delete this tender draft? This action cannot be undone.';

        modalRef.result.then((result) => {
            if (result) {
                this.tenderService.delete(this.tender).subscribe({
                    next: () => {
                        this.toast.success('Tender deleted successfully');
                        this.router.navigate(['/tenders']);
                    },
                    error: (e) => {
                        this.toast.error(e);
                    }
                });
            }
        }, () => { });
    }

    public goBack(): void {
        this.router.navigate(['/tenders']);
    }

}
